(ns ui.components.toolbar-button.view
  "A control in the toolbar of a listing, for example Group by, Views or Filters. It is
   the Button of ZapKit (`ui.components.button.view/component`, the variant `:ghost` in the
   size `:sm`) with a text, a chevron, and a count badge when the control has a setting, as
   the trigger of the Filters component of ZapKit and the buttons in the toolbar of the UX
   mockup. ZapKit has no component of this name. The control is
   one element at every width. From 1536 px (`2xl`) it shows the text. On a narrower screen
   it shows the icon in place of the text, and it keeps the chevron. The classes that hide
   and show the parts are classes of the old style sheet, because a breakpoint variant of
   the tw2 style sheet never matches, see
   the entry of 2026-10-22 about breakpoint variants in ai/mistakes.md."
  (:require
   [ui.components.badge.view :as badge]
   [ui.components.button.view :as button]
   [ui.icons :as icons]
   [ui.util :as uu]))

(def dom-overrides
  "Classes that the generated styles do not have. `:button` extends the ghost button of
   ZapKit: it is 36 px high with or without the count badge, it does not shrink, it has a
   maximum width, and its text does not wrap.
   `:icon` is the icon of a screen that is narrower than 1536 px. `:label` and `:suffix` are
   the text and the small indicator of a screen that is at least 1536 px wide.
   `:chevron` is the arrow of the popup, the ChevronDown of Lucide at 16 px as in the Filters of ZapKit."
  {:button  "tw2-h-9 tw2-shrink-0 tw2-max-w-[260px] tw2-whitespace-nowrap"
   :icon    "tw2-flex tw2-items-center tw2-leading-none 2xl:hidden"
   :label   "tw2-min-w-0 tw2-truncate hidden 2xl:inline"
   :suffix  "hidden 2xl:inline-flex tw2-min-w-0 tw2-items-center"
   :chevron "tw2-shrink-0 tw2-leading-none tw2-transition-transform"})

(defn view
  "Render the button.

   `:label` is the text. It is also the name of the button for a screen reader, unless
   `:aria-label` is given. `:icon` is the hiccup of the icon of a narrow screen. `:suffix` is
   a small indicator that is shown with the text. `:badge` is a count that is shown when it is
   not nil and not 0. `:expanded?` is the state of the popup, and it turns the chevron. `:popup`
   is the kind of the popup (`dialog` by default). `:title` is the tooltip."
  [{:keys [label icon suffix badge expanded? disabled? on-click aria-label title popup class]}]
  [button/component
   {:variant       :ghost
    :size          :sm
    :type          "button"
    :class         (uu/cm (:button dom-overrides)
                          class)
    :disabled?     (boolean disabled?)
    :aria-label    (or aria-label label)
    :aria-expanded (boolean expanded?)
    :aria-haspopup (or popup "dialog")
    :title         title
    :on-click      on-click}
   (when icon
     [:span {:class (:icon dom-overrides)}
      icon])
   [:span {:class (:label dom-overrides)}
    label]
   (when suffix
     [:span {:class (:suffix dom-overrides)}
      suffix])
   (when (and badge
              (not= 0 badge))
     [badge/view {:variant :success}
      badge])
   [:span
    {:class       (uu/cm (:chevron dom-overrides)
                         (when expanded?
                           "tw2-rotate-180"))
     :aria-hidden true}
    [icons/chevron-down-icon
     {:class "tw2-h-4 tw2-w-4"}]]])
